import {
  EditHaulRateDto,
  EditSurchargesDto,
  EditTransferRateDto,
  FuelSurchargeRangeDto,
  HaulRateDto,
  SurchargeToSave,
  TransferRateDto,
} from 'common/api/dto/rates.dto';
import { Pool } from 'common/models';
import { ActivePoolStatuses } from '../../SeasonsView/SeasonsView';
import { HaulRateValues } from '../Tabs/HaulRates';
import { perBinStr, perLoadStr, SurchargeValues } from '../Tabs/Surcharges';
import { TransferRateEdit } from '../Tabs/TransferRates';

const noIdStr = 'null';

type SurchargeStrType = typeof perBinStr | typeof perLoadStr;

export type TransferRatesByRange = {
  rangeId: number;
  rate: number;
};

/**
 * The helper function to create the key used by `useForm` to map the surcharge
 * form value to the correct input.
 */
export const createSurchargeKey = (
  type: SurchargeStrType,
  rangeId: number,
  surchargeId: number | null,
): string => `${type}_${rangeId}_${surchargeId || noIdStr}`;

/** The helper function to parse metadata from `useForm`'s dynamic keys. */
export const parseSurchargeKey = (
  key: string,
): {
  type: SurchargeStrType;
  rangeId: number;
  surchargeId: number | null;
} => {
  const [type, rangeId, surchargeId] = key.split('_');

  return {
    type: type as SurchargeStrType,
    rangeId: parseInt(rangeId, 10),
    surchargeId: parseInt(surchargeId, 10) || null,
  };
};

/**
 * The helper function to create the key used by `useForm` to map the rate
 * form value to the correct input.
 */
export const createRateKey = (
  rangeId: number,
  rateId?: number | null,
): string => `${rangeId}_${rateId || noIdStr}`;

/** The helper function to parse metadata from `useForm`'s dynamic keys. */
export const parseRateKey = (
  key: string,
): {
  rangeId: number;
  rateId: number | null;
} => {
  const [rangeId, rateId] = key.split('_');

  return {
    rangeId: parseInt(rangeId, 10),
    rateId: parseInt(rateId, 10) || null,
  };
};

export const formatDefaultSurcharges = (
  surcharges: FuelSurchargeRangeDto[],
): SurchargeValues => {
  return surcharges.reduce((acc, curr) => {
    const { haulLabelId, surchargeId, perBin, perLoad } = curr;

    acc[createSurchargeKey(perBinStr, haulLabelId, surchargeId)] = perBin;
    acc[createSurchargeKey(perLoadStr, haulLabelId, surchargeId)] = perLoad;

    return acc;
  }, {} as SurchargeValues);
};

export const formatDefaultRates = (
  rates:
    | Pick<HaulRateDto, 'id' | 'rangeId' | 'rate'>[]
    | Pick<TransferRateDto, 'id' | 'rangeId' | 'rate'>[],
): HaulRateValues | TransferRateEdit => {
  return rates.reduce(
    (acc, curr) => {
      const { id, rangeId, rate } = curr;

      acc[createRateKey(rangeId, id)] = rate;

      return acc;
    },
    {} as HaulRateValues | TransferRateEdit,
  );
};

export const formatDefaultPools = (pools: Pool[]): ActivePoolStatuses => {
  return pools.reduce((acc, curr) => {
    const { id, active } = curr;

    acc[id] = active;

    return acc;
  }, {} as ActivePoolStatuses);
};

export const toEditSurchargesDto = (
  periodId: number | null,
  formValues: SurchargeValues,
): EditSurchargesDto => {
  const surcharges = Object.entries(formValues).reduce(
    (acc, [key, surcharge]) => {
      const { type, rangeId, surchargeId } = parseSurchargeKey(key);

      if (!surchargeId && (surcharge === null || surcharge === 0)) {
        // If both bin and load rate are $0, avoid sending rate to the server.
        return acc;
      }

      const metadata = {
        haulLabelId: rangeId,
        ...(!surchargeId ? {} : { id: surchargeId }),
      };
      // There are two types of surcharges per range label - determine if
      // we've already added the other surcharge type.
      const existingFscIdx = acc.findIndex(
        s => s.haulLabelId === metadata.haulLabelId,
      );

      if (existingFscIdx > -1) {
        const otherSurcharge =
          type === perBinStr
            ? { perBin: surcharge || 0 }
            : { perLoad: surcharge || 0 };

        acc[existingFscIdx] = { ...acc[existingFscIdx], ...otherSurcharge };
      } else {
        // Because the DB only accepts numbers for surcharges, if one surcharge
        // type exists, we set the other `undefined` one to 0. If the other
        // exists as well, it will be updated later in the loop.
        acc.push({
          ...metadata,
          ...(type === perBinStr
            ? { perBin: surcharge || 0, perLoad: 0 }
            : { perLoad: surcharge || 0, perBin: 0 }),
        });
      }

      return acc;
    },
    [] as SurchargeToSave[],
  );

  return {
    periodId,
    surcharges,
  };
};

export const toEditHaulRatesDto = (
  formValues: HaulRateValues,
  defaultValues: HaulRateValues,
): EditHaulRateDto[] =>
  Object.entries(formValues).reduce((acc, [key, rate]) => {
    const { rangeId, rateId } = parseRateKey(key);

    if (!rateId && (rate === null || rate === 0)) {
      return acc;
    }

    if (defaultValues[key] !== rate) {
      acc.push({
        id: rateId,
        rangeId,
        rate: rate || 0,
      });
    }

    return acc;
  }, [] as EditHaulRateDto[]);

export const toEditTransferRatesDto = (
  defaultRates: TransferRateDto[],
  formValues: TransferRateEdit,
  getFieldState: (key: string) => { isDirty: boolean },
): EditTransferRateDto[] => {
  const groupRates: {
    isDirty: boolean;
    rangeId: number;
    rate: number;
    rateIds: number[];
  }[] = [];

  // Group all rates by their range ID and determine whether to set new rate.
  Object.entries(formValues).forEach(([key, rate]) => {
    const { rangeId, rateId } = parseRateKey(key);

    if (rateId) {
      const group = groupRates.find(group => group.rangeId === rangeId);
      const { isDirty } = getFieldState(key);

      if (group) {
        group.isDirty = isDirty;
        group.rateIds = [...group.rateIds, rateId];

        if (isDirty) {
          group.rate = rate || 0;
        }
      } else {
        groupRates.push({
          isDirty,
          rangeId,
          rate: rate || 0,
          rateIds: [rateId],
        });
      }
    }
  });

  // Filter out rates to save and map to DTO.
  const dtos = groupRates.reduce(
    (acc, group) => {
      if (group.isDirty) {
        acc.push(
          ...group.rateIds.map(id => ({
            id,
            rangeId: group.rangeId,
            rate: group.rate,
          })),
        );
      }

      return acc;
    },
    [] as { id: number; rangeId: number; rate: number }[],
  );

  // Attach old rate's route name and tarp flag to the 'new' rate.
  return dtos.map(dto => {
    const defaultRate = defaultRates.find(r => r.id === dto.id);

    if (defaultRate) {
      return {
        ...dto,
        route: defaultRate.route,
        chargeTarps: defaultRate.chargeTarps,
      };
    }

    return dto;
  }) as EditTransferRateDto[];
};

export const filterRatesByRange = (
  rates: TransferRateDto[],
): TransferRatesByRange[] =>
  rates.reduce((acc, curr) => {
    const { rangeId, rate } = curr;

    const existingRate = acc.find(r => r.rangeId === rangeId);

    if (!existingRate) {
      acc.push({
        rangeId,
        rate,
      });
    }

    return acc;
  }, [] as TransferRatesByRange[]);

export const toEditActivePoolsDto = (
  getFieldState: (key: string) => { isDirty: boolean },
  formValues: ActivePoolStatuses,
) =>
  Object.entries(formValues).reduce(
    (acc, [id, value]) => {
      if (getFieldState(id).isDirty) {
        acc.push({
          id: parseInt(id, 10),
          active: value,
        });
      }
      return acc;
    },
    [] as { id: number; active: boolean }[],
  );
